<template>
  <div class="tm-timemachine" @scroll.passive="$_onScroll">
    <tm-grid />
    <tm-empty-view
      v-if="$props.showEmptyView"
      :lightboxIsEmpty="$props.lightboxIsEmpty"
    />

    <tm-tracks />
    <tm-anchors />

    <tm-secondhand v-if="!$props.showEmptyView" />

    <tm-scroll-sensor />
    <tm-diag />
  </div>
</template>

<script>
import _ from 'lodash-extended';
import { mapState, mapActions, mapGetters } from 'vuex';
import { Axis } from './api/grid';
import { setStyleProperty, isTimeInsideTrack } from './api/tapi';
import tmBus from './api/tmBus';
import tmGrid from './view/grid/tmGrid.vue';
import tmSecondhand from './view/indicator/tmSecondhand.vue';
import tmDiag from './view/diag/tmDiag.vue';
import gridMixin from './view/grid.mixin';
import tmScrollSensor from './view/indicator/tmScrollSensor.vue';
import tmAnchors from './view/track/anchors/tmAnchors.vue';
import tmTracks from './view/track/tmTracks.vue';
import tmEmptyView from './view/tmEmptyView.vue';

export default {
  name: 'tm-timemachine',
  mixins: [gridMixin],
  components: {
    tmGrid,
    tmTracks,
    tmAnchors,
    tmSecondhand,
    tmScrollSensor,
    tmDiag,
    tmEmptyView,
  },

  props: {
    showEmptyView: { type: Boolean, required: true },
    lightboxIsEmpty: { type: Boolean, required: true },
  },

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      MODE_RCP_MSG_PLAY: (state) => state.MODE.RCP_MSG_PLAY,
      MODE_SECONDHAND_CUT: (state) => state.MODE.SECONDHAND_CUT,
      cssVariables: (state) => state.cssVariables,
      CONST: (state) => state.CONST,
      secondhand: (state) => state.secondhand,
      pxRowHeight: (state) => state.cssVariables.pxRowHeight,
      scrollTop: (state) => state.viewOffsets.scrollTop,
      sceneTrack: (state) => state.sceneEscort.sceneTrack,
      multiSelectIndex: (state) => state.dndEscort.multiSelectIndex,
      selected: (state) => state.selected,
    }),
    ...mapGetters('timemachine', [
      'isDndMode',
      'shouldShow_TrackLayerAudio',
      'shouldShow_TrackLayerTranscript',
    ]),
  },

  watch: {
    cssVariables: {
      handler: '$_setDynamicCss',
      deep: true,
    },

    gridEstablished(yes) {
      this.$emit('ready', yes);
    },

    shouldShow_TrackLayerAudio() {
      this.SET_TIME_LAYER_ROWS();
    },

    shouldShow_TrackLayerTranscript() {
      this.SET_TIME_LAYER_ROWS();
    },

    scrollTop(value) {
      this.$el.scrollTop = value;
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.$_onResize);
    document.removeEventListener('mouseup', this.$_onDocumentMouseUp);
    tmBus.neverMind(this);
  },

  created() {
    window.addEventListener('resize', this.$_onResize, { passive: true });
    document.addEventListener('mouseup', this.$_onDocumentMouseUp);
    tmBus.mind(
      this,
      [
        'callbackChange',
        'callbackRCPSeek',
        'callbackSecondhandDragStop',
        'callbackAssetResizeStart',
        'callbackAssetResizeUpdate',
        'callbackAssetResizeStop',
        'callbackAudioAssetResizeStart',
        'callbackAssetRemoved',
        'callbackAudioRemoved',
        'callbackEditAsset',
        'callbackEditSegment',
        'callbackEditTranscript',
        'callbackAssetSplitStart',
        'callbackAssetSplitStop',
        'callbackAudioAssetSplitStart',
        'callbackBeforeDragStarts',
        'callbackBeforeAddingExternalAsset',
        'callbackAfterAddingExternalAsset',
        'callbackEnterSceneEdit',
        'callbackLeaveSceneEdit',
        'callbackApplySceneEdit',
        'callbackCancelSceneEdit',
        'callbackSceneOverlayInserted',
        'callbackSceneUpdate',
        'callbackOverlayResizeStart',
        'callbackOverlayResizeUpdate',
        'callbackOverlayResizeStop',
        'callbackGetFragmentCutTimes',
      ],
      true
    );
  },

  mounted() {
    this.$_setDynamicCss();
    this.$_setZIndexCss();
    this.$nextTick(this.$_setAxis);
  },

  methods: {
    ...mapActions('timemachine', [
      'SET_AXIS',
      'SET_SCROLL_TOP',
      'SET_TIME_LAYER_ROWS',
      'SET_SECONDHAND',
      'SET_MODE',
      'SELECT_VISUAL',
      'SELECT_AUDIO',
      'UNSELECT_TRACKS',
      'SELECT_OVERLAY',
      'SCENE_EDIT_MODE_ENTER',
      'SCENE_EDIT_MODE_APPLY',
      'TRACK_NUMBER_CHANED',
      'MANAGED_AUDIO_INSERT_EXIT',
      'AUDIO_MULTISELECT_EXIT',
    ]),

    $_onScroll(e) {
      this.SET_SCROLL_TOP(this.$el.scrollTop);
    },

    $_onResize: _.debounce(
      function (e) {
        this.$_setAxis();
      },
      200,
      { leading: false, trailing: true }
    ),

    $_onDocumentMouseUp(e) {
      if (
        e.button !== 0 ||
        this.isDndMode ||
        e.target.closest(this.CONST.CLICK_OUTSIDE_IGNORABLE_SELECTORS)
      ) {
        return;
      }

      if (this.MODE_SECONDHAND_CUT) {
        this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });
      }

      if (this.MODE_SCENE_EDIT) {
        this.$_clickedWhileInSceneEdit(e);
      } else {
        if (this.MODE_OPTIONAL_AUDIO_INSERT) {
          this.MANAGED_AUDIO_INSERT_EXIT();
        }

        const CLICKED_INSIDE = e.target.closest('.tm-timemachine');
        if (CLICKED_INSIDE) {
          this.$_clickedWhileInside(e);
        } else {
          this.UNSELECT_TRACKS();
          if (this.MODE_AUDIO_MULTISELECT) {
            this.AUDIO_MULTISELECT_EXIT();
          }
        }
      }
    },

    $_clickedWhileInSceneEdit: function (e) {
      const CLICKED_INSIDE = e.target.closest('.tm-timemachine');
      const CLICKED_VISUAL_OVERLAY = e.target.closest('.tm-overlay');
      const mousePoint = this.$_mouse2point(e);
      const time = mousePoint.time;
      let shouldApply = true;

      if (CLICKED_INSIDE) {
        shouldApply = !isTimeInsideTrack(time, this.sceneTrack);
      }

      if (shouldApply) {
        this.SCENE_EDIT_MODE_APPLY({ keepItSelected: true }).then(
          ({ sceneTrack }) => {
            tmBus.$emit('callbackApplySceneEdit', {
              e,
              asset: sceneTrack.data,
              analyticsData: { confirm: 'v', using: 'click' },
            });
          }
        );
      } else {
        if (CLICKED_VISUAL_OVERLAY) {
          this.SELECT_OVERLAY({ time, log: e.metaKey });
        } else {
          this.SELECT_OVERLAY({ overlay: null });
        }

        this.SET_SECONDHAND({ time });
        tmBus.$emit('callbackRCPSeek', { time, seekImmediate: true });
      }
    },

    $_clickedWhileInside: function (e) {
      const CLICKED_VISUAL =
        e.target.closest('.tm-visual') && !e.target.closest('.tm-visual-text');
      const CLICKED_AUDIO =
        e.target.closest('.tm-audio') && !e.target.closest('.tm-audio-text');
      const CLICKED_VISUAL_OVERLAY = e.target.closest('.tm-overlay');
      const mousePoint = this.$_mouse2point(e);
      const time = mousePoint.time;
      const gotoTime = time;

      if (CLICKED_VISUAL) {
        const track = this.$_time2visualTrack(time);

        if (track) {
          const isSelectedTrack = this.selected.visual?.guid === track.guid;

          if (e.metaKey) {
            console.$log('visual', track);
            // console.$log(
            //   'visual',
            //   track.data.agd.customOverlays?.[0]?.fields?.[0]
            // );
          } else if (isSelectedTrack) {
            if (CLICKED_VISUAL_OVERLAY && !this.MODE_RCP_MSG_PLAY) {
              this.SCENE_EDIT_MODE_ENTER({ track }).then(({ sceneTrack }) => {
                tmBus.$emit('callbackEnterSceneEdit', {
                  asset: sceneTrack.data,
                  autopilotEntering: false,
                });
                this.SELECT_OVERLAY({ time });
              });
            }
          } else {
            this.SELECT_VISUAL({ track });
          }
        }

        if (this.MODE_AUDIO_MULTISELECT) {
          this.AUDIO_MULTISELECT_EXIT();
        }
      } else if (CLICKED_AUDIO) {
        const track = this.$_time2audioTrack(time);
        if (track) {
          if (e.metaKey) {
            console.$log('audio', track);
          } else {
            if (this.MODE_AUDIO_MULTISELECT) {
              const trackIndex = this.trackLayer.audio.indexOf(track);
              if (trackIndex < this.multiSelectIndex) {
                this.AUDIO_MULTISELECT_EXIT();
              }
            }

            this.SELECT_AUDIO({ track });
          }
        }
      } else if (e.metaKey) {
        this.SET_MODE({ name: 'DIAGNOSTIC', toggle: true });
      } else {
        this.UNSELECT_TRACKS();
        if (this.MODE_AUDIO_MULTISELECT) {
          this.AUDIO_MULTISELECT_EXIT();
        }
      }

      this.SET_SECONDHAND({ time: gotoTime });
      tmBus.$emit('callbackRCPSeek', { time: gotoTime, seekImmediate: true });
    },

    async $_setAxis() {
      await this.SET_AXIS(new Axis(this.$el, document.documentElement));
      this.SET_TIME_LAYER_ROWS();
    },

    $_setDynamicCss() {
      _.each(this.cssVariables, (value, prop) => {
        setStyleProperty(this.$el, prop, value);
      });
    },

    $_setZIndexCss() {
      _.each(this.CONST.Z_ORDER, (stack) => {
        _.each(stack, (prop, index) => {
          if (prop) {
            setStyleProperty(this.$el, prop, index);
          }
        });
      });
    },
  },
};
</script>

<style lang="scss">
.tm-timemachine {
  // one place stop for definition of all static variables used in subcomponents
  // benefits to discoverability
  // for definition of dynamic once refer to timemachine.vuex.js
  --tm-floorBackground: #f2f2f2;
  --tm-rulerColour: rgba(0, 0, 0, 0.5);
  --tm-rulerBorder: rgba(0, 0, 0, 0.1);
  --tm-btnDisabledColour: rgba(0, 0, 0, 0.3);
  --tm-replaceColour: #{$color-green-m};
  --tm-semMarkColour: rgba(0, 0, 0, 0.1);
  --tm-rulerBackground: #e6e6e6;
  --tm-audioTrackBackground: rgba(0, 0, 0, 0.05);
  --tm-audioTrackHover: rgba(0, 0, 0, 0.1);
  --tm-higlightedTrackBackground: rgba(0, 0, 0, 0.1);
  --tm-audioBorder1: rgba(0, 0, 0, 0.25);
  --tm-audioBorder2: #{$color-primary};
  --tm-audioBorderW1: 1px;
  --tm-audioBorderW2: 2px;
  --tm-pxSceneEditH: 58px;

  // set axis to top-left corner, reset z-index stacking context
  position: relative;
  margin: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
  height: calc(100vh - 413px);
  user-select: none;
  background-color: var(--tm-floorBackground);

  i {
    // normalize css for default element of the timeline
    font-style: normal;
    display: block;
  }

  &,
  * {
    cursor: var(--tm-cursor);
  }
}
</style>
